import type { CSSProperties, KeyboardEvent, MouseEvent } from 'react';
import { useEffect, useRef, useState } from 'react';
import { Locale } from '../../app/locale/resolveLocale';
import { brandQrPath, brandUrl } from '../../data/brand';
import { findPersonalityType, regularTypes } from '../../data/taxonomy';
import type { ScoreVector } from '../../data/dimensions';
import { resultExperience } from '../../data/resultExperience';
import { visualAssets } from '../../data/visualAssets';
import { resultEvidence } from '../../engine/resultEvidence';
import type { ScoreEventInput } from '../../engine/scoring';
import { generateShareImagePreview } from '../shareBuilder/shareImage';

const templateLabels: Record<Locale, Array<{ id: string; label: string; ratio: string }>> = {
  'zh-CN': [
    { id: 'poster_report', label: '推荐图', ratio: '3:4' },
    { id: 'square_badge', label: '方形图', ratio: '1:1' },
    { id: 'story_hero', label: '竖屏图', ratio: '9:16' }
  ],
  en: [
    { id: 'poster_report', label: 'Poster', ratio: '3:4' },
    { id: 'square_badge', label: 'Square', ratio: '1:1' },
    { id: 'story_hero', label: 'Story', ratio: '9:16' }
  ]
};

const resultCopy: Record<Locale, {
  kicker: string;
  profileButton: string;
  profileHeading: string;
  close: string;
  shareImageAction: string;
  copyLinkAction: string;
  copyLinkCopied: string;
  copyLinkFallback: string;
  previewHeading: string;
  previewHint: string;
  previewAlt: (label: string) => string;
  previewClose: string;
  previewStatus: string;
  home: string;
  playAgain: string;
}> = {
  'zh-CN': {
    kicker: '社交事故档案',
    profileButton: '查看详解',
    profileHeading: '人格详解',
    close: '关闭',
    shareImageAction: '显示分享图片',
    copyLinkAction: '复制链接',
    copyLinkCopied: '链接已复制',
    copyLinkFallback: `链接：${brandUrl}`,
    previewHeading: '分享图预览',
    previewHint: '长按图片保存，或直接转发给朋友',
    previewAlt: (label) => `${label}预览`,
    previewClose: '关闭预览',
    previewStatus: '正在生成预览...',
    home: '回首页',
    playAgain: '再玩一次'
  },
  en: {
    kicker: 'Social Mishap Report',
    profileButton: 'Details',
    profileHeading: 'Full Profile',
    close: 'Close',
    shareImageAction: 'Show share image',
    copyLinkAction: 'Copy link',
    copyLinkCopied: 'Link copied',
    copyLinkFallback: `Link: ${brandUrl}`,
    previewHeading: 'Share preview',
    previewHint: 'Long-press the image to save or forward',
    previewAlt: (label) => `${label} preview`,
    previewClose: 'Close preview',
    previewStatus: 'Rendering preview...',
    home: 'Home',
    playAgain: 'Play again'
  }
};

const focusableDialogSelector =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

function focusableElementsIn(dialog: HTMLElement) {
  return Array.from(dialog.querySelectorAll<HTMLElement>(focusableDialogSelector)).filter(
    (element) => !element.hasAttribute('disabled') && element.tabIndex !== -1
  );
}

function keepTabFocusInside(event: KeyboardEvent<HTMLElement>, dialog: HTMLElement) {
  if (event.key !== 'Tab') return;

  const focusableElements = focusableElementsIn(dialog);
  if (focusableElements.length === 0) {
    event.preventDefault();
    dialog.focus();
    return;
  }

  const firstElement = focusableElements[0];
  const lastElement = focusableElements[focusableElements.length - 1];
  const activeElement = document.activeElement;

  if (!dialog.contains(activeElement)) {
    event.preventDefault();
    firstElement.focus();
    return;
  }

  if (event.shiftKey && activeElement === firstElement) {
    event.preventDefault();
    lastElement.focus();
    return;
  }

  if (!event.shiftKey && activeElement === lastElement) {
    event.preventDefault();
    firstElement.focus();
  }
}

export function ResultCards({
  typeId,
  locale,
  scores,
  events = []
}: {
  typeId: string;
  locale: Locale;
  scores?: ScoreVector;
  events?: ScoreEventInput[];
}) {
  const [shareTemplate, setShareTemplate] = useState('poster_report');
  const [previewRequestId, setPreviewRequestId] = useState(0);
  const [sharePreviewUrl, setSharePreviewUrl] = useState<string | null>(null);
  const [copyLinkStatus, setCopyLinkStatus] = useState<string | null>(null);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const profileDialogRef = useRef<HTMLElement | null>(null);
  const profileCloseButtonRef = useRef<HTMLButtonElement | null>(null);
  const profileOpenButtonRef = useRef<HTMLButtonElement | null>(null);
  const sharePreviewDialogRef = useRef<HTMLElement | null>(null);
  const sharePreviewCloseButtonRef = useRef<HTMLButtonElement | null>(null);
  const sharePreviewTriggerRef = useRef<HTMLElement | null>(null);
  const copyLinkStatusTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const type = findPersonalityType(typeId) ?? regularTypes[0];
  const asset = visualAssets[type.typeId];
  const displayName = locale === 'zh-CN' ? type.zhShare : type.enShare;
  const detailName = locale === 'zh-CN' ? type.zhDetail : type.enDetail;
  const experience = resultExperience(type, locale, scores);
  const selectedTemplate = templateLabels[locale].find((template) => template.id === shareTemplate) ?? templateLabels[locale][0];
  const copy = resultCopy[locale];
  const style = {
    '--accent-primary': asset.primary,
    '--accent-secondary': asset.secondary
  } as CSSProperties;
  const evidence = resultEvidence(type, locale, events);

  useEffect(() => {
    if (previewRequestId === 0) return undefined;
    let cancelled = false;

    setSharePreviewUrl(null);
    generateShareImagePreview('[data-share-card]')
      .then((dataUrl) => {
        if (!cancelled) setSharePreviewUrl(dataUrl);
      })
      .catch(() => {
        if (!cancelled) setSharePreviewUrl(null);
      });

    return () => {
      cancelled = true;
    };
  }, [previewRequestId, shareTemplate]);

  useEffect(() => {
    if (isProfileOpen) profileCloseButtonRef.current?.focus();
  }, [isProfileOpen]);

  useEffect(() => {
    if (previewRequestId > 0) sharePreviewCloseButtonRef.current?.focus();
  }, [previewRequestId]);

  useEffect(
    () => () => {
      if (copyLinkStatusTimeoutRef.current) clearTimeout(copyLinkStatusTimeoutRef.current);
    },
    []
  );

  function showCopyLinkStatus(message: string, dismissAfterMs?: number) {
    if (copyLinkStatusTimeoutRef.current) clearTimeout(copyLinkStatusTimeoutRef.current);
    setCopyLinkStatus(message);

    if (!dismissAfterMs) {
      copyLinkStatusTimeoutRef.current = null;
      return;
    }

    copyLinkStatusTimeoutRef.current = setTimeout(() => {
      setCopyLinkStatus(null);
      copyLinkStatusTimeoutRef.current = null;
    }, dismissAfterMs);
  }

  function handleShareImageOpen() {
    const activeElement = document.activeElement;
    sharePreviewTriggerRef.current = activeElement instanceof HTMLElement ? activeElement : null;
    setShareTemplate('poster_report');
    setPreviewRequestId((requestId) => requestId + 1);
  }

  function handlePreviewTemplateSelect(templateId: string) {
    setShareTemplate(templateId);
    setPreviewRequestId((requestId) => requestId + 1);
  }

  async function handleCopyLink() {
    try {
      if (!navigator.clipboard?.writeText) throw new Error('Clipboard API unavailable');
      await navigator.clipboard.writeText(brandUrl);
      showCopyLinkStatus(copy.copyLinkCopied, 1000);
    } catch {
      showCopyLinkStatus(copy.copyLinkFallback);
    }
  }

  function handlePreviewClose() {
    setPreviewRequestId(0);
    setSharePreviewUrl(null);
    if (sharePreviewTriggerRef.current?.isConnected) sharePreviewTriggerRef.current.focus();
  }

  function closeProfileDialog() {
    setIsProfileOpen(false);
    if (profileOpenButtonRef.current?.isConnected) profileOpenButtonRef.current.focus();
  }

  function handleProfileDialogKeyDown(event: KeyboardEvent<HTMLElement>) {
    if (event.key === 'Escape') {
      event.stopPropagation();
      closeProfileDialog();
      return;
    }

    if (event.key !== 'Tab') return;

    const dialog = profileDialogRef.current;
    if (dialog) keepTabFocusInside(event, dialog);
  }

  function handleSharePreviewKeyDown(event: KeyboardEvent<HTMLElement>) {
    if (event.key === 'Escape') {
      event.stopPropagation();
      handlePreviewClose();
      return;
    }

    const dialog = sharePreviewDialogRef.current;
    if (dialog) keepTabFocusInside(event, dialog);
  }

  function handleSharePreviewBackdropMouseDown(event: MouseEvent<HTMLDivElement>) {
    if (event.target === event.currentTarget) handlePreviewClose();
  }

  return (
    <div className="result-story" style={style}>
      <section className="result-report">
        <div className="result-art-shell">
          <img src={asset.hero} alt={`${displayName} character illustration`} className="result-character" />
        </div>
        <div className="result-copy">
          <p className="result-kicker">{copy.kicker}</p>
          <h1>{displayName}</h1>
          <p className="result-detail">{detailName}</p>
          <p className="result-punchline">{experience.punchline}</p>
          <div className="result-tags" aria-label="behavior tags">
            {experience.tags.map((tag) => (
              <span key={tag}>{tag}</span>
            ))}
          </div>
          <section className="result-evidence-summary" aria-label={evidence.heading}>
            <p>{evidence.heading}</p>
            <dl>
              <div>
                <dt>{evidence.primary.label}</dt>
                <dd>{evidence.primary.text}</dd>
              </div>
              <div>
                <dt>{evidence.followUp.label}</dt>
                <dd>{evidence.followUp.text}</dd>
              </div>
              <div>
                <dt>{evidence.triggers.label}</dt>
                <dd>{evidence.triggers.text}</dd>
              </div>
            </dl>
            <div className="result-risk-repair">
              <p>
                <strong>{evidence.risk.label}</strong>
                {evidence.risk.text}
              </p>
              <p>
                <strong>{evidence.repair.label}</strong>
                {evidence.repair.text}
              </p>
            </div>
          </section>
          <div className="result-secondary-actions" aria-label={locale === 'zh-CN' ? '结果操作' : 'Result actions'}>
            <button
              className="profile-open-button"
              ref={profileOpenButtonRef}
              type="button"
              onClick={() => setIsProfileOpen(true)}
            >
              {copy.profileButton}
            </button>
            <a href="/play">{copy.playAgain}</a>
            <a href="/">{copy.home}</a>
          </div>
          <div className="result-share-actions" aria-label={locale === 'zh-CN' ? '分享操作' : 'Share actions'}>
            <button className="share-action-button primary" type="button" onClick={handleShareImageOpen}>
              {copy.shareImageAction}
            </button>
            <button className="share-action-button" type="button" onClick={handleCopyLink}>
              {copy.copyLinkAction}
            </button>
            {copyLinkStatus ? <span className="share-copy-status">{copyLinkStatus}</span> : null}
          </div>
        </div>
      </section>

      {previewRequestId > 0 ? (
        <div className="result-modal-backdrop share-preview-backdrop" onMouseDown={handleSharePreviewBackdropMouseDown}>
          <section
            aria-label={copy.previewHeading}
            aria-modal="true"
            className="result-modal-panel share-preview-panel"
            onKeyDown={handleSharePreviewKeyDown}
            ref={sharePreviewDialogRef}
            role="dialog"
            tabIndex={-1}
          >
            <button
              aria-label={copy.previewClose}
              className="result-modal-close"
              ref={sharePreviewCloseButtonRef}
              type="button"
              onClick={handlePreviewClose}
            >
              ×
            </button>
            <div className="panel-heading compact">
              <div>
                <h2>{copy.previewHeading}</h2>
                <span className="share-save-hint">{copy.previewHint}</span>
              </div>
            </div>
            <div className="share-template-selector" role="group" aria-label={locale === 'zh-CN' ? '分享图片样式' : 'Share image style'}>
              {templateLabels[locale].map((template) => (
                <button
                  aria-pressed={shareTemplate === template.id}
                  className="template-button"
                  key={template.id}
                  onClick={() => handlePreviewTemplateSelect(template.id)}
                  type="button"
                >
                  <span>{template.label}</span>
                  <strong>{template.ratio}</strong>
                </button>
              ))}
            </div>
            <div className="share-preview" aria-live="polite">
              {!sharePreviewUrl ? <p>{copy.previewStatus}</p> : null}
              {sharePreviewUrl ? (
                <img
                  alt={copy.previewAlt(selectedTemplate.label)}
                  src={sharePreviewUrl}
                  style={{ aspectRatio: selectedTemplate.ratio.replace(':', ' / ') }}
                />
              ) : null}
            </div>
          </section>
        </div>
      ) : null}

      {previewRequestId > 0 ? (
        <div className="share-render-host" aria-hidden="true">
          <section className="share-card-canvas" data-share-card data-share-template={shareTemplate}>
            <div className="share-card-art">
              <img src={asset.hero} alt="" />
            </div>
            <div className="share-card-copy">
              <p>{copy.kicker}</p>
              <h2>{displayName}</h2>
              <strong>{detailName}</strong>
              <span>{experience.punchline}</span>
              <div>
                {experience.tags.slice(0, 3).map((tag) => (
                  <i key={tag}>{tag}</i>
                ))}
              </div>
            </div>
            <div className="share-card-qr-stamp">
              <img className="share-card-qr" src={brandQrPath} alt="" />
            </div>
          </section>
        </div>
      ) : null}

      {isProfileOpen ? (
        <div className="result-modal-backdrop profile-dialog-backdrop">
          <section
            aria-label={displayName}
            aria-modal="true"
            className="result-modal-panel profile-dialog"
            onKeyDown={handleProfileDialogKeyDown}
            ref={profileDialogRef}
            role="dialog"
            tabIndex={-1}
          >
            <button
              aria-label={copy.close}
              className="result-modal-close"
              ref={profileCloseButtonRef}
              type="button"
              onClick={closeProfileDialog}
            >
              ×
            </button>
            <p className="profile-dialog-kicker">{copy.profileHeading}</p>
            <h2>{displayName}</h2>
            <strong>{detailName}</strong>
            <div className="profile-variants">
              {experience.profileVariants.map((variant) => (
                <p data-testid="profile-variant" key={variant}>
                  {variant}
                </p>
              ))}
            </div>
          </section>
        </div>
      ) : null}
    </div>
  );
}
